import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { AIMood, MoodOrderItem } from './types';
import MoodCard from './MoodCard';
import { MAX_ACTIVE_MOODS } from './constants';

interface MoodCardGridProps {
  orderedItems: MoodOrderItem[];
  allMoods: AIMood[];
  loading: boolean;
  onToggle: (id: number, currentActive: boolean) => void;
  onEdit: (mood: AIMood) => void;
  onDelete: (id: number) => void;
}

const MoodCardGrid: React.FC<MoodCardGridProps> = ({
  orderedItems,
  allMoods,
  loading,
  onToggle,
  onEdit,
  onDelete,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', p: 2 }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={120} sx={{ borderRadius: '10px' }} />
        ))}
      </Box>
    );
  }

  const activeOrdered = orderedItems.filter(i => i.isActive);
  const inGridIds = new Set(activeOrdered.slice(0, MAX_ACTIVE_MOODS).map(i => i.id));

  return (
    <Box sx={{ p: 2, overflowY: 'auto', minHeight: 0 }}>
      <SortableContext items={orderedItems.map(i => i.id)} strategy={rectSortingStrategy}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
          {orderedItems.map((orderItem, idx) => {
            const fullMood = allMoods.find(m => m.id === orderItem.id);
            if (!fullMood) return null;

            const activePosition = activeOrdered.findIndex(i => i.id === orderItem.id);
            const inGrid = inGridIds.has(orderItem.id);
            const position = idx + 1;
            const isMostPopular = activePosition === 0;

            // Insert divider before first non-in-grid item
            const prevItem = idx > 0 ? orderedItems[idx - 1] : null;
            const prevInGrid = prevItem ? inGridIds.has(prevItem.id) : true;
            const showDivider = !inGrid && prevInGrid && idx > 0;

            return (
              <React.Fragment key={orderItem.id}>
                {showDivider && (
                  <Box
                    sx={{
                      gridColumn: '1 / -1',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1,
                      px: 1,
                    }}
                  >
                    <Box sx={{ flex: 1, height: 1, bgcolor: '#d1d5db' }} />
                    <Typography
                      sx={{
                        fontFamily: '"Montserrat", sans-serif',
                        fontSize: '0.68rem',
                        color: '#94a3b8',
                        whiteSpace: 'nowrap',
                        fontWeight: 500,
                      }}
                    >
                      Not in app grid — drag card up or into a grid slot
                    </Typography>
                    <Box sx={{ flex: 1, height: 1, bgcolor: '#d1d5db' }} />
                  </Box>
                )}
                <MoodCard
                  mood={fullMood}
                  position={position}
                  inGrid={inGrid}
                  isMostPopular={isMostPopular}
                  onToggle={onToggle}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              </React.Fragment>
            );
          })}
        </Box>
      </SortableContext>
    </Box>
  );
};

export default MoodCardGrid;
